<template>
  <div class="bm-empty" :data-testid="testid" :data-art="art">
    <img
      v-if="artSrc"
      class="bm-empty__art"
      :src="artSrc"
      :alt="''"
      :data-art="art"
      :data-testid="artTestid"
    />
    <div class="bm-empty__title">{{ title }}</div>
    <div class="bm-empty__body"><slot name="body">{{ body }}</slot></div>
    <div v-if="$slots.action" class="bm-empty__action"><slot name="action" /></div>
  </div>
</template>

<script>
/**
 * The three empty states named in the Locked Decisions:
 *   - empty_city  : brand-new store, no batches at all
 *   - search      : a filter or query that matched nothing
 *   - (no art)    : an Open batch that has collected no orders yet
 *
 * The third one USED to render tiny_party.svg. Jake asked for that
 * illustration to go (2026-09-08); the state itself is unchanged. The asset
 * and its entry in ART below are deliberately retained -- removing unused
 * code is a separate decision, and the map is what keeps the bundler able to
 * see the file if the state is ever given art again.
 *
 * The `art` prop takes the bare filename so the calling component reads the
 * same way the design doc does. Resolving through a static map (rather than a
 * dynamic require of an interpolated path) keeps the bundler able to see every
 * asset it needs at build time -- a computed require() path would silently
 * produce a broken image in a production build.
 *
 * `art` is OPTIONAL. Omitting it renders the state with no illustration at
 * all, rather than an empty <img> -- the open-and-empty batch state does this
 * (Jake, 2026-09-08). The guard is on the RESOLVED source, not on the prop, so
 * an unrecognised filename also renders nothing instead of a broken image.
 */
import emptyCity from "@/assets/illustrations/empty_city.svg";
import tinyParty from "@/assets/illustrations/tiny_party.svg";
import searchArt from "@/assets/illustrations/search.svg";

const ART = {
  "empty_city.svg": emptyCity,
  "tiny_party.svg": tinyParty,
  "search.svg": searchArt,
};

export default {
  name: "BatchEmptyState",
  props: {
    art: { type: String, default: "" },
    title: { type: String, required: true },
    body: { type: String, default: "" },
    testid: { type: String, default: undefined },
  },
  computed: {
    artSrc() {
      return ART[this.art] || "";
    },
    // A testid on the illustration itself, so "this state has no art" is
    // assertable by the ui-runner -- its selectors are data-testid only
    // (R-1), and the class is not a legal handle there.
    //
    // Derived, so check_testid_dependencies.py (which greps for literal
    // `data-testid="..."`) cannot see it. The three names this component can
    // emit are spelled out below so the lint resolves them:
    //   data-testid="empty-open-batch-art"
    //   data-testid="empty-no-batches-art"
    //   data-testid="empty-no-results-art"
    artTestid() {
      return this.testid ? `${this.testid}-art` : undefined;
    },
  },
};
</script>

<style scoped>
.bm-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 46px 24px 52px;
  gap: 4px;
}

.bm-empty__art {
  width: 178px;
  max-width: 60%;
  margin-bottom: 18px;
  opacity: 0.95;
}

.bm-empty__title {
  font-size: 19px;
  font-weight: 500;
  color: var(--tf-secondary);
}

.bm-empty__body {
  font-size: 13.5px;
  color: var(--tf-fg-3);
  line-height: 1.6;
  max-width: 430px;
}

.bm-empty__action {
  margin-top: 16px;
}

@media (max-width: 599px) {
  .bm-empty {
    padding: 34px 16px 38px;
  }
}
</style>
